import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, Mail, Phone, Building, RefreshCw, Check, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { dashApi } from '../api';

const FORM_TYPE_LABELS = {
  contact: 'Contact',
  quote: 'Request a Quote',
  fast_quote: 'Fast Quote',
  roi_playbook: 'ROI Playbook',
  schedule_call: 'Schedule a Call',
  consultation_intake: 'Consultation Intake',
  consultation_booking: 'Consultation Booking',
  custom_project: 'Custom Project',
  request_tester: 'Request Tester',
  sample_request: 'Sample Request',
  quick_ship_sample: 'Quick Ship Sample',
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
};

const FailedSubmissionsPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('unresolved');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await dashApi.get('/failed-submissions', {
        params: { status: statusFilter, limit: 100 },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleResolve = async (id) => {
    await dashApi.post(`/failed-submissions/${id}/resolve`, { note: 'Reached customer' });
    await load();
  };

  const handleReopen = async (id) => {
    await dashApi.post(`/failed-submissions/${id}/reopen`);
    await load();
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="failed-submissions-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            Failed Submissions
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Lead-capture attempts that hit a network or server error and never reached the inbox.
            Reach out manually, then mark as resolved. Click <span className="font-medium">View session</span> to replay
            what the customer was doing in Microsoft Clarity.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {['unresolved', 'resolved', 'all'].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={statusFilter === s ? 'default' : 'outline'}
              onClick={() => setStatusFilter(s)}
              data-testid={`failed-status-${s}`}
              className="capitalize"
            >
              {s}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={load} data-testid="failed-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card className="p-0">
        {loading && (
          <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="failed-empty-state">
            {statusFilter === 'unresolved'
              ? '🎉 No unresolved failures. Every recent submission reached your inbox.'
              : 'No records.'}
          </div>
        )}
        {!loading && items.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {items.map((it) => (
              <li
                key={it.id}
                className="p-4 md:p-5 hover:bg-slate-50/60 transition-colors"
                data-testid={`failed-row-${it.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="bg-amber-100 text-amber-900 border-amber-200">
                        {FORM_TYPE_LABELS[it.formType] || it.formType}
                      </Badge>
                      <span className="text-xs text-slate-500">{relativeTime(it.created_at)}</span>
                      {it.errorReason && (
                        <span className="text-xs text-slate-500 truncate" title={it.errorReason}>
                          · {it.errorReason}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 font-medium text-slate-900">
                      {it.name || it.email || '(no identity captured)'}
                    </div>
                    <div className="mt-1 flex items-center gap-4 flex-wrap text-sm text-slate-600">
                      {it.email && (
                        <a href={`mailto:${it.email}`} className="flex items-center gap-1 hover:text-primary">
                          <Mail className="h-3.5 w-3.5" /> {it.email}
                        </a>
                      )}
                      {it.phone && (
                        <a href={`tel:${it.phone}`} className="flex items-center gap-1 hover:text-primary">
                          <Phone className="h-3.5 w-3.5" /> {it.phone}
                        </a>
                      )}
                      {it.company && (
                        <span className="flex items-center gap-1">
                          <Building className="h-3.5 w-3.5" /> {it.company}
                        </span>
                      )}
                    </div>
                    {it.draft && Object.keys(it.draft).length > 0 && (
                      <details className="mt-3">
                        <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                          What they were typing (view draft)
                        </summary>
                        <pre className="mt-2 text-xs bg-slate-50 border border-slate-100 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                          {Object.entries(it.draft)
                            .filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
                            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n')}
                        </pre>
                      </details>
                    )}
                    {it.resolved_by && (
                      <div className="mt-2 text-xs text-emerald-700">
                        Resolved by {it.resolved_by}{it.resolved_at ? ` · ${relativeTime(it.resolved_at)}` : ''}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {it.clarityReplayUrl && (
                      <a
                        href={it.clarityReplayUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        data-testid={`failed-clarity-${it.id}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View session
                      </a>
                    )}
                    {it.status === 'unresolved' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolve(it.id)}
                        data-testid={`failed-resolve-${it.id}`}
                      >
                        <Check className="h-3.5 w-3.5 mr-1" /> Mark resolved
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleReopen(it.id)}
                        data-testid={`failed-reopen-${it.id}`}
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <p className="text-xs text-slate-400">
        Showing {items.length} of {total} · <Link to="/dashboard/submissions" className="hover:underline">Back to submissions →</Link>
      </p>
    </div>
  );
};

export default FailedSubmissionsPage;
